/**
 * PRP-NOTES-1: review chat's suggested notes and set the estimate's note level.
 *
 * Contract: GET / PATCH /api/v1/me/estimates/{parse_hash}/notes (app/api/routes/note_review.py).
 * PATCH takes any of `note_level` ("none" | "brief" | "detailed", or "account" to use the account
 * default), `lines` and `room_notes`, and returns the same view as GET. 409 = the estimate changed
 * since it was read. Same auth/fetch path as importMetadataApi.ts; failures arrive as `ApiError`.
 */
import { api } from './api';

export type NoteLevel = 'none' | 'brief' | 'detailed';
export type ReviewAction = 'accept' | 'edit' | 'skip' | 'reset';

export interface SuggestedLineNote {
  room_index: number;
  line_item_index: number;
  room: string;
  line_number: number;
  code: string;
  description: string;
  suggested_note: string | null;
  reason: string | null;
  review: 'accepted' | 'skipped' | null;
  prints: boolean;
  held_reason: string | null;
  internal_notes: string[];
}

export interface SuggestedRoomNote {
  room_index: number;
  room: string;
  text: string;
  reason: string | null;
  review: 'accepted' | 'skipped' | null;
  prints: boolean;
  held_reason: string | null;
}

export interface RoomInternalNotes {
  room_index: number;
  room: string;
  internal_notes: string[];
}

export interface NoteReview {
  note_level: NoteLevel;
  note_level_source: 'estimate' | 'account' | 'default';
  account_level: NoteLevel;
  levels: { key: NoteLevel; label: string }[];
  reasons: Record<string, string>;
  lines: SuggestedLineNote[];
  room_notes: SuggestedRoomNote[];
  room_internal_notes: RoomInternalNotes[];
}

export interface NoteReviewPatch {
  note_level?: NoteLevel | 'account';
  /** `line_number` is the line the page showed; the server answers 409 if that position now holds another line. */
  lines?: { room_index: number; line_item_index: number; line_number: number; action: ReviewAction; text?: string }[];
  room_notes?: { room_index: number; text: string; action: ReviewAction; new_text?: string }[];
}

const url = (parseHash: string) => `/me/estimates/${encodeURIComponent(parseHash)}/notes`;

export const getNoteReview = async (parseHash: string): Promise<NoteReview> => api.get(url(parseHash));

export const patchNoteReview = async (parseHash: string, body: NoteReviewPatch): Promise<NoteReview> =>
  api.patch(url(parseHash), body);
